import React from 'react';
import {
  Controller,
  useFormContext,
} from 'react-hook-form';
import dayjs from 'utils/dayjs';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import type {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import AllowanceEditor from 'views/EMR/components/Authorizations/AllowanceEditor';
import type {
  AuthorizationFormValues,
} from 'views/EMR/components/Authorizations/formUtils';

type Props = {
  patient: PatientForm;
  disabled?: boolean;
};

function validateExpirationDate(
  expirationDate: AuthorizationFormValues['expirationDate'],
  effectiveDate: AuthorizationFormValues['effectiveDate'],
) {
  return !expirationDate
    || !effectiveDate
    || !dayjs(expirationDate).isBefore(dayjs(effectiveDate), 'day')
    || 'Expiration date cannot be before effective date';
}

export default function AuthorizationForm({ patient, disabled = false }: Props) {
  const {
    control,
    getValues,
  } = useFormContext<AuthorizationFormValues>();

  return (
    <div className="grid grid-cols-2 gap-form-field">
      <Controller
        control={control}
        name="authorizationNumber"
        rules={{ required: 'Enter authorization number' }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            id="authorization-number"
            label="Authorization number"
            className="col-span-2"
            required
            disabled={disabled}
            error={Boolean(error)}
            showErrorText={false}
            inputProps={{ maxLength: 64 }}
          />
        )}
      />
      <Controller
        control={control}
        name="insuranceId"
        rules={{
          required: 'Select insurance',
          validate: (value) => Number(value) > 0 || 'Select insurance',
        }}
        render={({ field, fieldState: { error } }) => (
          <Wrapper
            id="authorization-insurance"
            label="Insurance"
            required
            error={Boolean(error)}
            showErrorText={false}
            className="col-span-2"
          >
            <PatientInsuranceSelect
              patientId={patient.patientId ?? 0}
              insuranceId={field.value ?? 0}
              hasDefault={false}
              editable={false}
              disabled={disabled}
              className="w-full"
              classes={{ select: '!text-left' }}
              detailedView
              onChange={(option) => {
                const selected = Array.isArray(option) ? option[0] : option;
                field.onChange(selected?.insuranceId ?? null);
              }}
            />
          </Wrapper>
        )}
      />
      <Controller
        control={control}
        name="effectiveDate"
        rules={{ required: 'Enter effective dates' }}
        render={({ field: effectiveDateField, fieldState: { error: effectiveDateError } }) => (
          <Controller
            control={control}
            name="expirationDate"
            rules={{
              required: 'Enter effective dates',
              validate: (value) => validateExpirationDate(
                value,
                getValues('effectiveDate'),
              ),
              deps: ['effectiveDate'],
            }}
            render={({ field: expirationDateField, fieldState: { error: expirationDateError } }) => (
              <Wrapper
                id="authorization-effective-dates"
                label="Effective dates"
                required
                error={Boolean(effectiveDateError || expirationDateError)}
                showErrorText={false}
                className="col-span-2"
              >
                <DateRangePicker
                  value={effectiveDateField.value && expirationDateField.value ? {
                    min: effectiveDateField.value.toDate(),
                    max: expirationDateField.value.toDate(),
                  } : null}
                  onChange={(dateRange) => {
                    effectiveDateField.onChange(dateRange ? dayjs(dateRange.min) : null);
                    expirationDateField.onChange(dateRange ? dayjs(dateRange.max) : null);
                  }}
                  disabled={disabled}
                  maxDate={null}
                  showPresets={false}
                  placeholder="Select date range"
                  autoFocusDateInput
                  classes={{ root: 'w-full', rangePicker: '!w-full !max-w-none' }}
                />
              </Wrapper>
            )}
          />
        )}
      />
      <AllowanceEditor disabled={disabled} />
      <Controller
        control={control}
        name="notes"
        render={({ field }) => (
          <TextAreaWithActions
            {...field}
            id="authorization-notes"
            label="Notes"
            className="col-span-2"
            trackingLabel="Authorization Notes"
            placeholder="Your message is here..."
          />
        )}
      />
    </div>
  );
}
